if (!customElements.get('product-purchase-option')) {
  customElements.define(
    'product-purchase-option',
    class ProductPurchaseOption extends HTMLElement {
      constructor() {
        super();

        this.clickTab = this.clickTab.bind(this);
        this.clickNoSubmitBtn = this.clickNoSubmitBtn.bind(this);
        this.clickCloseSlsPopupBtn = this.clickCloseSlsPopupBtn.bind(this);

        this.intervalId = null;
        this.isSealWidgetUSPLoaded = false;

        this.pricesForSubscription = {
          price: 0,
          unitPrice: 0,
        };
        this.pricesForBundle = {
          price: 0,
          unitPrice: 0,
        };

        this.selectedTab = 'js-purchase-tab';
        this.hasBulkBuyOption = false;
        this.hasSubscriptionOption = false;
        this.initialSetupComplete = false;
      }

      connectedCallback() {
        // Check for data attributes first - these are the most reliable indicators
        if (this.dataset.hasBulkBuy === 'true') {
          this.hasBulkBuyOption = true;
        }

        if (this.dataset.hasSubscription === 'true') {
          this.hasSubscriptionOption = true;
        }

        // Check if subscription option is available from the DOM
        if (!this.hasSubscriptionOption) {
          const subscriptionTab = this.querySelector('.js-purchase-option-tab[data-selector="js-subscribe-tab"]');
          if (subscriptionTab && !subscriptionTab.classList.contains('hidden')) {
            this.hasSubscriptionOption = true;
          }
        }

        // Set up event listeners
        this.querySelectorAll('.js-purchase-option-tab').forEach((elem) => {
          elem.addEventListener('click', (event) => this.clickTab(event));
        });

        const closeSlsPopupBtn = document.querySelector('.sls-subscriptions-more-info__close-btn');
        if (closeSlsPopupBtn) closeSlsPopupBtn.addEventListener('click', (event) => this.clickCloseSlsPopupBtn(event));

        // Initialize prices
        this.setInitialPrices();

        // Hide variant select by default
        this.hideVariantSelect();

        // IMPORTANT: Always start with the purchase tab active by default
        // This ensures we always show bulk buy first if it becomes available
        this.activatePurchaseTab();

        // Start observers for dynamic content
        setTimeout(() => {
          this.observeSealWidget();
          this.observeBundleAppWidget();

          // After a short delay, finalize the tab setup based on what was detected
          setTimeout(() => {
            this.finalizeTabSetup();
          }, 1000);
        }, 100);
      }

      activatePurchaseTab() {
        const purchaseTab = this.querySelector('.js-purchase-option-tab[data-selector="js-purchase-tab"]');
        const purchaseContent = this.querySelector('.js-tab-content.js-purchase-tab');
        const subscriptionTab = this.querySelector('.js-purchase-option-tab[data-selector="js-subscribe-tab"]');
        const subscriptionContent = this.querySelector('.js-tab-content.js-subscribe-tab');

        if (purchaseTab) {
          purchaseTab.classList.add('purchase-option-tab--active');
          purchaseTab.classList.remove('hidden');
        }

        if (purchaseContent) {
          purchaseContent.classList.remove('hidden');
        }

        if (subscriptionTab) {
          subscriptionTab.classList.remove('purchase-option-tab--active');
        }

        if (subscriptionContent) {
          subscriptionContent.classList.add('hidden');
        }

        this.selectedTab = 'js-purchase-tab';
        this.hideVariantSelect();
      }

      finalizeTabSetup() {
        if (this.initialSetupComplete) return;

        console.log(
          'Finalizing tab setup - Bulk Buy:',
          this.hasBulkBuyOption,
          'Subscription:',
          this.hasSubscriptionOption
        );

        const subscriptionTab = this.querySelector('.js-purchase-option-tab[data-selector="js-subscribe-tab"]');
        const purchaseTab = this.querySelector('.js-purchase-option-tab[data-selector="js-purchase-tab"]');
        const subscriptionContent = this.querySelector('.js-tab-content.js-subscribe-tab');
        const purchaseContent = this.querySelector('.js-tab-content.js-purchase-tab');

        // Case 1: Both options available - ALWAYS prioritize bulk buy
        if (this.hasBulkBuyOption && this.hasSubscriptionOption) {
          // Make sure bulk buy tab is active
          this.activatePurchaseTab();

          // Just make sure prices are updated
          const { price, unitPrice } = this.pricesForBundle;
          this.updatePrices(price, unitPrice);
        }
        // Case 2: Only subscription available
        else if (!this.hasBulkBuyOption && this.hasSubscriptionOption) {
          if (subscriptionTab) subscriptionTab.classList.add('purchase-option-tab--active');
          if (purchaseTab) purchaseTab.classList.remove('purchase-option-tab--active');

          if (subscriptionContent) subscriptionContent.classList.remove('hidden');
          if (purchaseContent) purchaseContent.classList.add('hidden');

          this.selectedTab = 'js-subscribe-tab';
          this.showVariantSelect();

          // Update prices for subscription
          const { price, unitPrice } = this.pricesForSubscription;
          this.updatePrices(price, unitPrice);
        }
        // Case 3: Only bulk buy available - already set up in connectedCallback
        else if (this.hasBulkBuyOption && !this.hasSubscriptionOption) {
          // Make sure bulk buy tab is active
          this.activatePurchaseTab();

          // Just make sure prices are updated
          const { price, unitPrice } = this.pricesForBundle;
          this.updatePrices(price, unitPrice);
        }
        // Case 4: Neither option available - hide both tabs
        else {
          if (purchaseTab) purchaseTab.classList.add('hidden');
          if (subscriptionTab) subscriptionTab.classList.add('hidden');
        }

        this.initialSetupComplete = true;
      }

      setupInitialTabState() {
        // Just call finalizeTabSetup to ensure consistent behavior
        if (!this.initialSetupComplete) {
          this.finalizeTabSetup();
        }
      }

      // Replace the old checkAndSetDefaultTab with a simpler function
      checkAndSetDefaultTab() {
        if (!this.initialSetupComplete) {
          this.finalizeTabSetup();
        }
      }

      getVariantData() {
        // First try to get data from variant-selects (for products with variants)
        const variantSelects = document.querySelectorAll('variant-selects');
        if (variantSelects && variantSelects.length > 0) {
          const variantSelectsElem = variantSelects[0];
          if (variantSelectsElem) {
            const scriptTag = variantSelectsElem.querySelector('script[data-selected-variant]');
            if (scriptTag) {
              const scriptJson = safeParseJSON(scriptTag.textContent);
              if (scriptJson) return scriptJson;
            }
          }
        }

        // For products without variants, try to get data from hidden input
        const variantIdInput = document.querySelector('input[type="hidden"][name="id"].product-variant-id');
        if (variantIdInput) {
          const variantId = variantIdInput.value;

          // Try to find product data from a script tag
          const productDataScript = document.querySelector('#p-data');
          if (productDataScript) {
            const productData = safeParseJSON(productDataScript.textContent);
            if (productData && productData.data) {
              // Find the variant in the product data
              const variant = productData.data.variants.find((v) => v.id.toString() === variantId.toString());
              if (variant) {
                return variant;
              }
            }
          }

          // If we can't find product data from script tag, try to get price from the page
          const priceElement = document.querySelector('.price_info--wrapper .main_price--wrapper .main_price');
          const unitPriceElement = document.querySelector('.price_info--wrapper .unit_price');

          if (priceElement) {
            // Create a minimal variant object with just the price information
            const price = this.extractPriceValue(priceElement.textContent);
            const unitPrice = unitPriceElement ? this.extractPriceValue(unitPriceElement.textContent) : price;

            return {
              id: variantId,
              price,
              unit_price: unitPrice,
              selling_plan_allocations: [],
            };
          }
        }

        console.error('Could not find variant data for product');
        return null;
      }

      extractPriceValue(priceString) {
        if (!priceString) return 0;
        // Extract only numbers from the price string
        const matches = priceString.match(/[\d,.]+/);
        if (matches && matches.length > 0) {
          // Convert price string to number (cents)
          const price = parseFloat(matches[0].replace(/[,.]/g, '.')) * 100;
          return Math.round(price);
        }
        return 0;
      }

      setInitialPrices() {
        const scriptJson = this.getVariantData();
        if (scriptJson && scriptJson.price && scriptJson.unit_price) {
          this.pricesForSubscription = {
            price: scriptJson.price,
            unitPrice: scriptJson.unit_price,
          };

          this.pricesForBundle = {
            price: scriptJson.price,
            unitPrice: scriptJson.unit_price,
          };
          return;
        }

        // If we couldn't get variant data, try to extract prices directly from the page
        const priceElement = document.querySelector('.price_info--wrapper .main_price--wrapper .main_price');
        const unitPriceElement = document.querySelector('.price_info--wrapper .unit_price');

        if (priceElement) {
          const price = this.extractPriceValue(priceElement.textContent);
          const unitPrice = unitPriceElement ? this.extractPriceValue(unitPriceElement.textContent) : price;

          this.pricesForSubscription = {
            price,
            unitPrice,
          };

          this.pricesForBundle = {
            price,
            unitPrice,
          };
        } else {
          console.error('Could not find price information on the page');
        }
      }

      observeBundleAppWidget() {
        // observer callback to watch if the Dealeasy Discount App Widget is loaded initially
        const targetNode = document.querySelector('.dealeasy-discount-app-form');
        if (!targetNode) return;

        const callback = (mutationList, observer) => {
          for (const mutation of mutationList) {
            if (mutation.type === 'childList') {
              const pumperBundleAppWidget = document.querySelector('lb-vd-qty-selector');
              if (pumperBundleAppWidget) {
                const tab = this.querySelector('.js-purchase-option-tab[data-selector="js-purchase-tab"]');
                if (tab) {
                  tab.classList.remove('hidden');
                  this.hasBulkBuyOption = true;

                  // If we have both options, make sure bulk buy is active
                  if (this.hasSubscriptionOption) {
                    this.activatePurchaseTab();
                  }
                }

                if (this.intervalId) {
                  clearInterval(this.intervalId);
                  this.intervalId = null;
                }

                this.intervalId = setInterval(() => {
                  const atcButton = document.querySelector('.lb-qty-selector-atc-button');
                  if (atcButton) {
                    this.observeBundleAppAtcButton();

                    atcButton.classList.add('golden-gradient-ani-btn');
                    atcButton.classList.add('btn_cta');

                    atcButton.innerHTML = `<span>${atcButton.innerHTML}</span>`;

                    clearInterval(this.intervalId);
                    this.intervalId = null;
                  }
                }, 500);

                observer.disconnect();
              }
            }
          }
        };

        const observer = new MutationObserver(callback);
        observer.observe(targetNode, { attributes: true, childList: true, subtree: true });
      }

      observeBundleAppAtcButton() {
        const targetNode = document.querySelector('.lb-qty-selector-atc-button');
        if (!targetNode) return;

        const callback = (mutationList, observer) => {
          for (const mutation of mutationList) {
            if (
              mutation.type === 'childList' &&
              targetNode.innerHTML.indexOf('<span>') < 0 &&
              targetNode.innerHTML.indexOf('<svg>') < 0
            ) {
              targetNode.innerHTML = `<span>${targetNode.innerHTML}</span>`;
            }
          }
        };

        const observer = new MutationObserver(callback);
        observer.observe(targetNode, { attributes: true, childList: true, subtree: true });

        document.addEventListener('lb-selected-tier-changed', this.onBundleTierChanged.bind(this));
      }

      observeSealWidget() {
        // observer callback to watch if the Seal Subscriptions App Widget is loaded initially
        const targetNode = document.querySelector('.js-subscribe-tab .sealsubs-target-element[data-handle]');
        if (!targetNode) return;

        const callback = (mutationList, observer) => {
          for (const mutation of mutationList) {
            if (mutation.type === 'childList' && mutation.target.querySelector('.sls-selling-plan-group-options')) {
              setTimeout(() => {
                this.onSealWidgetLoad();
                this.hasSubscriptionOption = true;
                observer.disconnect();
              }, 100);
            }
          }
        };

        const observer = new MutationObserver(callback);
        observer.observe(targetNode, { attributes: true, childList: true, subtree: true });

        const intervalId = setInterval(() => {
          if (targetNode.querySelector('.sls-selling-plan-group-options')) {
            this.onSealWidgetLoad();
            this.hasSubscriptionOption = true;
            observer.disconnect();
            clearInterval(intervalId);
          }
        }, 500);
      }

      clickCloseSlsPopupBtn(event) {
        const modalDialog = document.querySelector('#SlsSubscriptionsMoreInfo');
        if (!modalDialog) return;

        modalDialog.hide();

        const slsPopupModalOverlay = document.querySelector('.sls-popup-modal-overlay');
        if (slsPopupModalOverlay) slsPopupModalOverlay.classList.add('hidden');
      }

      clickTab(event) {
        event.stopImmediatePropagation();

        this.querySelectorAll('.js-purchase-option-tab').forEach((elem) => {
          elem.classList.remove('purchase-option-tab--active');
        });
        event.currentTarget.classList.add('purchase-option-tab--active');

        const { selector } = event.currentTarget.dataset;
        this.selectedTab = selector;

        this.querySelectorAll('.js-purchase-option-tab-content .js-tab-content').forEach((elem) => {
          if (elem.classList.contains(selector)) elem.classList.remove('hidden');
          else elem.classList.add('hidden');
        });

        if (selector === 'js-subscribe-tab') {
          this.showVariantSelect();

          // Ensure we have valid prices for subscription
          if (!this.pricesForSubscription.price || this.pricesForSubscription.price <= 0) {
            // Try to get current prices from the page
            const priceElement = document.querySelector('.price_info--wrapper .main_price--wrapper .main_price');
            const unitPriceElement = document.querySelector('.price_info--wrapper .unit_price');

            if (priceElement) {
              const price = this.extractPriceValue(priceElement.textContent);
              const unitPrice = unitPriceElement ? this.extractPriceValue(unitPriceElement.textContent) : price;

              // Try to get subscription price from the selling plan options
              const sellingPlanDetails = document.querySelector(
                '.sls-selling-plan-group-options .sls-option-container input[type="radio"]:checked'
              );
              if (sellingPlanDetails && sellingPlanDetails.dataset.sellingPlanPrice) {
                const sellingPlanPrice = parseInt(sellingPlanDetails.dataset.sellingPlanPrice, 10);
                if (!isNaN(sellingPlanPrice)) {
                  // Calculate the unit price based on the same ratio
                  const ratio = unitPrice / price;
                  const sellingPlanUnitPrice = Math.round(sellingPlanPrice * ratio);

                  this.pricesForSubscription = {
                    price: sellingPlanPrice,
                    unitPrice: sellingPlanUnitPrice,
                  };
                }
              } else {
                // If no selling plan details available, just use the current prices
                this.pricesForSubscription = {
                  price,
                  unitPrice,
                };
              }
            }
          }

          const { price, unitPrice } = this.pricesForSubscription;
          this.updatePrices(price, unitPrice);
        } else {
          this.hideVariantSelect();

          // Ensure we have valid prices for bundle
          if (!this.pricesForBundle.price || this.pricesForBundle.price <= 0) {
            // Try to get current prices from the page
            const priceElement = document.querySelector('.price_info--wrapper .main_price--wrapper .main_price');
            const unitPriceElement = document.querySelector('.price_info--wrapper .unit_price');

            if (priceElement) {
              const price = this.extractPriceValue(priceElement.textContent);
              const unitPrice = unitPriceElement ? this.extractPriceValue(unitPriceElement.textContent) : price;

              this.pricesForBundle = {
                price,
                unitPrice,
              };
            }
          }

          const { price, unitPrice } = this.pricesForBundle;
          this.updatePrices(price, unitPrice);
        }
      }

      showVariantSelect() {
        const variantSelects = document.querySelectorAll('variant-selects.options_wrapper_container');

        variantSelects.forEach((element) => {
          if (!element.closest('.sticky_atc--container') && !element.closest('.sticky-atc__container-mobile')) {
            element.classList.remove('hidden');

            const variantSelectOption = element.querySelector('.options-pills__wrapper');
            if (variantSelectOption) variantSelectOption.classList.remove('hidden');
          }
        });
      }

      hideVariantSelect() {
        const variantSelects = document.querySelectorAll('variant-selects.options_wrapper_container');

        variantSelects.forEach((element) => {
          if (!element.closest('.sticky_atc--container') && !element.closest('.sticky-atc__container-mobile')) {
            element.classList.add('hidden');

            const variantSelectOption = element.querySelector('.options-pills__wrapper');
            if (variantSelectOption) variantSelectOption.classList.add('hidden');
          }
        });
      }

      onSealWidgetLoad() {
        const slsSubscriptionOptionContainers = this.querySelectorAll('.sls-option-container');
        if (!slsSubscriptionOptionContainers || slsSubscriptionOptionContainers.length === 0) return;

        const slsSubscriptionOptionContainer =
          slsSubscriptionOptionContainers[slsSubscriptionOptionContainers.length - 1];

        const sellingPlanFirstLine = slsSubscriptionOptionContainer.querySelector(
          '.seal-row .sls-label-container.seal-col .sls-selling-plan-group-first-line'
        );
        if (!sellingPlanFirstLine) return;

        let uspHTML = sellingPlanFirstLine.outerHTML;

        if (this.isSealWidgetUSPLoaded) return;

        this.isSealWidgetUSPLoaded = true;

        // usp list
        const scriptTag = this.querySelector('#usp-data');
        if (!scriptTag) {
          console.error('Script tag with ID "usp-data" not found.');
          return;
        }

        try {
          const json = safeParseJSON(scriptTag.textContent.trim());
          if (json) {
            const { usp_list } = json;

            if (usp_list) {
              const uspListElement = document.createElement('ul');
              uspListElement.classList.add('sls-usp-list');
              for (const usp of usp_list) {
                const listItem = document.createElement('li');
                listItem.classList.add('sls-usp-list__each');
                listItem.textContent = usp;
                uspListElement.appendChild(listItem);
              }
              uspHTML += uspListElement.outerHTML;
            }
          }
        } catch (error) {
          console.error('Invalid USP JSON string:', error);
        }

        const origMoreInfoText = this.querySelector('.sls-more-info-container');
        if (!origMoreInfoText) return;

        origMoreInfoText.classList.add('hidden');

        // more information link
        const slsMoreInfoPopupContentElem = this.querySelector('.sls-more-info-popup-content');
        if (!slsMoreInfoPopupContentElem) return;

        const slsMoreInfoPopupContent = slsMoreInfoPopupContentElem.innerHTML;

        const moreInfoText = this.dataset.moreInfo;
        uspHTML += `
          <div class="sls-subscriptions-more-info">
            <button class="sls-subscriptions-more-info__link no-submit-btn">
              ${moreInfoText}
            </button>
          </div>
        `;

        sellingPlanFirstLine.outerHTML = uspHTML;

        // update modal body
        const modalDialog = document.querySelector('#SlsSubscriptionsMoreInfo');
        if (!modalDialog) return;

        modalDialog.querySelector('.sls-popup-modal__body').innerHTML = slsMoreInfoPopupContent;

        // add event listener for the "More information" link
        const moreInfoLink = this.querySelector('.sls-subscriptions-more-info__link');

        const observerCallback = (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.addEventListener('click', (event) => this.clickNoSubmitBtn(event));

              observer.unobserve(entry.target);
            }
          });
        };

        const observerOptions = {
          root: null,
          rootMargin: '0px',
          threshold: 0.1,
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        if (moreInfoLink) observer.observe(moreInfoLink);

        this.addEventListenerForSellingPlanChange();
      }

      addEventListenerForSellingPlanChange() {
        // add event listener for changing selling plan
        const purchaseOptionContainer = this.querySelector('.sls-purchase-options-container');
        if (!purchaseOptionContainer) return;

        const sellingPlanHiddenInputElem = purchaseOptionContainer.querySelector(
          'input[type="hidden"][name="selling_plan"]'
        );
        if (!sellingPlanHiddenInputElem) return;

        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'value') {
              this.updatePricesWhenSellingPlanChange(sellingPlanHiddenInputElem);
            }
          });
        });

        observer.observe(sellingPlanHiddenInputElem, { attributes: true });
      }

      updatePricesWhenSellingPlanChange(sellingPlanHiddenInputElem) {
        if (!sellingPlanHiddenInputElem) return;

        const scriptJson = this.getVariantData();
        if (!scriptJson) {
          // If we can't get variant data, try to use the current prices
          const priceElement = document.querySelector('.price_info--wrapper .main_price--wrapper .main_price');
          const unitPriceElement = document.querySelector('.price_info--wrapper .unit_price');

          if (priceElement) {
            const price = this.extractPriceValue(priceElement.textContent);
            const unitPrice = unitPriceElement ? this.extractPriceValue(unitPriceElement.textContent) : price;

            // Use the values from the selling plan if available
            const sellingPlanDetails = document.querySelector(
              '.sls-selling-plan-group-options .sls-option-container input[type="radio"]:checked'
            );
            if (sellingPlanDetails && sellingPlanDetails.dataset.sellingPlanPrice) {
              const sellingPlanPrice = parseInt(sellingPlanDetails.dataset.sellingPlanPrice, 10);
              if (!isNaN(sellingPlanPrice)) {
                // Calculate the unit price based on the same ratio
                const ratio = unitPrice / price;
                const sellingPlanUnitPrice = Math.round(sellingPlanPrice * ratio);

                this.updatePrices(sellingPlanPrice, sellingPlanUnitPrice);
                return;
              }
            }

            // If no selling plan details available, just use the current prices
            this.updatePrices(price, unitPrice);
          }
          return;
        }

        if (!scriptJson.price || !scriptJson.unit_price) {
          console.error('Price information is missing in the variant data.');
          return;
        }

        if (sellingPlanHiddenInputElem.value.trim() === '') {
          this.updatePrices(scriptJson.price, scriptJson.unit_price);
        } else {
          const sellingPlanAllocations = scriptJson.selling_plan_allocations;
          if (!sellingPlanAllocations) {
            // If there are no selling plan allocations, use the regular price
            this.updatePrices(scriptJson.price, scriptJson.unit_price);
            return;
          }

          const selectedSellingPlan = sellingPlanAllocations.find(
            (elem) => elem.selling_plan_id == sellingPlanHiddenInputElem.value
          );
          if (!selectedSellingPlan) {
            // If we can't find the selected selling plan, use the regular price
            this.updatePrices(scriptJson.price, scriptJson.unit_price);
            return;
          }

          if (!selectedSellingPlan.price || !selectedSellingPlan.unit_price) {
            console.error('Price information is missing in the selected selling plan.');
            return;
          }

          this.updatePrices(selectedSellingPlan.price, selectedSellingPlan.unit_price);
        }
      }

      onBundleTierChanged(event) {
        if (this.selectedTab === 'js-subscribe-tab') return;

        if (!event || !event.detail) return;

        const { price, unitPrice } = event.detail;
        if (!price) {
          console.error('Price information is missing in the event details.');
          return;
        }

        // Store the bundle prices for later use
        this.pricesForBundle = {
          price: Math.round(price),
          unitPrice: unitPrice ? Math.round(unitPrice) : this.calculateUnitPrice(Math.round(price)),
        };

        this.updatePrices(
          Math.round(price),
          unitPrice ? Math.round(unitPrice) : this.calculateUnitPrice(Math.round(price))
        );
      }

      calculateUnitPrice(price) {
        // Calculate unit price based on the original ratio
        const originalUnitPriceElem = document.querySelector('.price_info--wrapper .unit_price');
        const originalMainPriceElem = document.querySelector('.price_info--wrapper .main_price--wrapper .main_price');

        if (
          originalUnitPriceElem &&
          originalMainPriceElem &&
          originalUnitPriceElem.getAttribute('data-original-text')
        ) {
          const originalUnitPrice = this.extractPriceValue(originalUnitPriceElem.getAttribute('data-original-text'));
          const originalMainPrice = this.extractPriceValue(originalMainPriceElem.textContent);

          if (originalUnitPrice > 0 && originalMainPrice > 0) {
            // Calculate the ratio between unit price and main price
            const ratio = originalUnitPrice / originalMainPrice;

            // Apply the same ratio to the new price
            return Math.round(price * ratio);
          }
        }

        return price; // Fallback to the same price if we can't calculate
      }

      clickNoSubmitBtn(event) {
        event.stopImmediatePropagation();
        event.preventDefault();

        const modalDialog = document.querySelector('#SlsSubscriptionsMoreInfo');
        if (!modalDialog) return;

        const slsPopupModalOverlay = document.querySelector('.sls-popup-modal-overlay');

        modalDialog.show(event.currentTarget);
        if (slsPopupModalOverlay) slsPopupModalOverlay.classList.remove('hidden');
      }

      updatePrices(price, unitPrice) {
        console.log('updatePrices', price, unitPrice);
        if (this.selectedTab === 'js-subscribe-tab') {
          this.pricesForSubscription = {
            price,
            unitPrice,
          };
        } else if (this.selectedTab === 'js-purchase-tab') {
          this.pricesForBundle = {
            price,
            unitPrice,
          };
        }

        // If we have a reference value, recalculate the unit price based on the main price
        const referenceValue = parseFloat(this.dataset.unitPriceReferenceValue);
        if (!isNaN(referenceValue) && referenceValue > 0) {
          // Get the original unit price and main price to determine the ratio
          const originalUnitPriceElem = document.querySelector('.price_info--wrapper .unit_price');
          const originalMainPriceElem = document.querySelector('.price_info--wrapper .main_price--wrapper .main_price');

          if (
            originalUnitPriceElem &&
            originalMainPriceElem &&
            originalUnitPriceElem.getAttribute('data-original-text')
          ) {
            const originalUnitPrice = this.extractPriceValue(originalUnitPriceElem.getAttribute('data-original-text'));
            const originalMainPrice = this.extractPriceValue(originalMainPriceElem.textContent);

            if (originalUnitPrice > 0 && originalMainPrice > 0) {
              // Calculate the ratio between unit price and main price
              const ratio = originalUnitPrice / originalMainPrice;

              // Apply the same ratio to the new price
              unitPrice = Math.round(price * ratio);
            }
          }
        }

        this.updateMainPrice(price);
        this.updateUnitPrice(unitPrice);
      }

      updateMainPrice(price) {
        const mainPriceElem = document.querySelector('.price_info--wrapper .main_price--wrapper .main_price');
        if (!mainPriceElem) return;

        mainPriceElem.textContent = Shopify.formatMoney(price);
      }

      updateUnitPrice(unitPrice) {
        const unitPriceElem = document.querySelector('.price_info--wrapper .unit_price');
        if (!unitPriceElem) return;

        const referenceValue = this.dataset.unitPriceReferenceValue || '';
        const referenceUnit = this.dataset.unitPriceReferenceUnit || '';
        unitPriceElem.textContent = `${Shopify.formatMoney(unitPrice)} / ${referenceValue}${referenceUnit}`;
      }
    }
  );
}
